import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CommonActions } from '@react-navigation/native';
import Modal from 'utils/packages/Modal';
import { setError } from 'actions';
import { internalTransfer, withdrawPotToConnectedAccount, } from 'features/pots/actions';
import { getNextTier } from 'features/premium/entities';
import { selectUserTier } from 'features/premium/selectors/tier';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { formatAmount, formatDateTime, formatNextBuyDate, } from 'features/invest/utils';
import { useFetchOld, muteAllButThrows, muteErrorAndResult, muteErrorAndFetching, } from 'hooks';
import { selectPotProductFromProductId } from 'features/pots/reducers/selectors';
import { getTradingAccounts, withdrawTradingAccount } from 'actions/invest';
import useWithPinAuthentication from 'hooks/useWithPinAuthentication';
import { getMyLimits } from '../actions';
import OverLimitModal from '../components/OverLimitModal';
import { InternalTransfer } from '../types';
import { isAndroid } from '../../../constants';
const DEFAULT_DELAY_TIME = isAndroid ? 100 : 0;
const routesToRemove = [
    'Transfer',
    'DepositToPotScreen',
    'SelectPotType',
    'CreatePot',
    'ReviewAndConfirm',
    'PotInterestRates',
];
const strings = {
    errorTitle: 'The\xa0payment\xa0didn’t\xa0go through. Please\xa0try\xa0again.',
    passcodeModalTitle: 'Enter Your Pin Code',
};
export const useTransfer = () => {
    const store = useAppStore();
    const userTier = useAppSelector(selectUserTier);
    const [loadingTransfer, setLoadingTransfer] = useState(false);
    const withPinAuthentication = useWithPinAuthentication();
    const navigationTimeoutRef = useRef(undefined);
    const navigationRemoveScreensTimeoutRef = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(navigationTimeoutRef.current);
        clearTimeout(navigationRemoveScreensTimeoutRef.current);
    }, []);
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const [, , loading, fetch] = useFetchOld(muteErrorAndResult);
    const [, , , fetchButThrows] = useFetchOld(muteAllButThrows);
    const [myLimits, , , fetchMyLimits] = useFetchOld(muteErrorAndFetching);
    const onIncreaseLimitPress = useCallback(() => {
        const nextTier = getNextTier(userTier);
        Modal.hide(() => {
            navigation.navigateDeprecated('EmmaProModal', {
                tier: nextTier,
                benefitId: 'pay',
            });
        });
    }, [userTier]);
    const onTransfer = useCallback(async (amount, reference, from, to) => {
        if (!myLimits ||
            (myLimits.remainingAllowanceNow !== null &&
                amount > myLimits.remainingAllowanceNow)) {
            setLoadingTransfer(true);
            try {
                const response = await fetchMyLimits(getMyLimits());
                if (response) {
                    if (response.remainingAllowanceNow !== null &&
                        response.monthly.limit !== null &&
                        amount > response.remainingAllowanceNow) {
                        Modal.show(<OverLimitModal usedAmount={response.monthly.used} currentLimit={response.monthly.limit} tryingToTransferAmount={amount} onIncreaseLimitPress={onIncreaseLimitPress}/>, { increasedTopPadding: true });
                        return;
                    }
                }
                else {
                    dispatch(setError('Error', 'This is awkward, please try again.'));
                    return;
                }
            }
            catch {
                return;
            }
            finally {
                setLoadingTransfer(false);
            }
        }
        navigation.navigateDeprecated('CreatePaymentScreen', {
            bankAccount: from,
            toBankAccount: to,
            amount,
            reference,
            options: { customSubtitle: `Your transfer to ${to.name}` },
            backCount: 3,
            type: 'transfer',
        });
    }, [fetchMyLimits, myLimits, onIncreaseLimitPress]);
    const removeIntermediateScreens = useCallback(() => {
        navigationRemoveScreensTimeoutRef.current = setTimeout(() => {
            navigation.dispatch((state) => {
                const routes = state.routes.filter((r) => !routesToRemove.includes(r.name));
                return CommonActions.reset({
                    ...state,
                    routes,
                    index: routes.length - 1,
                });
            });
        }, 400);
    }, []);
    const handleNavigate = useCallback((productId, amount, type, updatedPot, isTransfer, withdrawalETA, isBankTransfer) => {
        const dateString = withdrawalETA
            ? formatNextBuyDate(withdrawalETA)
            : undefined;
        const dateTimeString = withdrawalETA
            ? formatDateTime(withdrawalETA)
            : undefined;
        const { routes } = navigation.getState();
        const potRoute = routes.find((route) => route.name === 'Pot');
        if (potRoute) {
            navigation.dispatch({
                ...CommonActions.setParams({
                    screenParams: {
                        ...updatedPot,
                    },
                }),
                source: potRoute.key,
            });
        }
        const potProduct = selectPotProductFromProductId(store.getState(), productId);
        if (dateString &&
            dateTimeString &&
            potProduct?.partnerBank &&
            isTransfer) {
            navigationTimeoutRef.current = setTimeout(() => {
                navigation.navigateDeprecated('WithdrawTimeline', {
                    productId,
                    dateString,
                    dateTimeString,
                    isBankTransfer,
                    withdrawAmount: amount,
                    emoji: updatedPot.emoji,
                    potName: updatedPot.name,
                    formattedInitiatedTime: 'Today',
                    bankName: potProduct.partnerBank || '',
                });
            }, DEFAULT_DELAY_TIME);
            // Removing the intermediate screens
            removeIntermediateScreens();
        }
        else {
            navigationTimeoutRef.current = setTimeout(() => {
                navigation.navigateDeprecated('InternalTransferSuccess', {
                    type,
                    amount,
                    date: new Date(),
                });
            }, DEFAULT_DELAY_TIME);
            // Removing the intermediate screens
            removeIntermediateScreens();
        }
    }, [removeIntermediateScreens]);
    const onInternalTransfer = useCallback(async (amount, from, to, type) => {
        const response = await fetch(internalTransfer(from.id, to.id, {
            value: amount,
            currency: from.balance.currency,
        }));
        if (response?.pots) {
            const potId = type === InternalTransfer.withdraw ? from.id : to.id;
            const updatedActivePot = response.pots.find((pot) => pot.id === potId);
            handleNavigate(to.productId, amount, type, updatedActivePot);
        }
    }, [handleNavigate]);
    const handleWithdrawPotToConnectedAccount = useCallback((pot, amount, forcePinAuthentication) => withPinAuthentication({
        title: strings.passcodeModalTitle,
        biometryDescription: `You are withdrawing ${formatAmount({
            value: amount,
            currency: pot.balance.currency,
        })}`,
        errorTitle: strings.errorTitle,
        forcePinAuthentication,
        run: async (pin) => {
            try {
                setLoadingTransfer(true);
                const response = await fetchButThrows(withdrawPotToConnectedAccount(pot.id, amount, pot.balance.currency, pin));
                if (response?.pot) {
                    handleNavigate(pot.productId, amount, InternalTransfer.withdraw, response.pot, true, response?.withdrawalETA, true);
                }
            }
            finally {
                setLoadingTransfer(false);
            }
        },
    }), [fetchButThrows, handleNavigate, withPinAuthentication]);
    const onWithdrawSecclAccount = useCallback((id, amount, connectedAccount, forcePinAuthentication) => withPinAuthentication({
        title: strings.passcodeModalTitle,
        biometryDescription: `You are withdrawing ${formatAmount({
            value: amount,
            currency: 'GBP',
        })}`,
        errorTitle: strings.errorTitle,
        forcePinAuthentication,
        run: async (pin) => {
            try {
                setLoadingTransfer(true);
                await fetchButThrows(withdrawTradingAccount(id, amount, pin));
                await dispatch(getTradingAccounts());
                navigation.pop();
                navigation.navigateDeprecated('WithdrawFromAccountTimeline', {
                    amount,
                    connectedAccount,
                    date: new Date(),
                });
            }
            finally {
                setLoadingTransfer(false);
            }
        },
    }), [dispatch, fetchButThrows, withPinAuthentication]);
    return {
        onTransfer,
        onInternalTransfer,
        onWithdrawSecclAccount,
        handleWithdrawPotToConnectedAccount,
        requesting: loading || loadingTransfer,
    };
};
